// Parsing/validation helpers for the supported video sources: YouTube,
// Vimeo, hosted MP4 files, and plain external links (a page we can't or
// shouldn't embed — e.g. a demo hosted on the product's own site). Click-to-
// play only — we never generate an autoplaying embed, and never render a
// player for a URL we can't confidently classify.

function getYouTubeId(url) {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') return u.pathname.slice(1).split('/')[0] || null;
    if (/(^|\.)youtube\.com$/.test(u.hostname)) {
      if (u.pathname === '/watch') return u.searchParams.get('v');
      if (u.pathname.startsWith('/embed/')) return u.pathname.split('/embed/')[1]?.split('/')[0] || null;
      if (u.pathname.startsWith('/shorts/')) return u.pathname.split('/shorts/')[1]?.split('/')[0] || null;
    }
    return null;
  } catch {
    return null;
  }
}

function getVimeoId(url) {
  try {
    const u = new URL(url);
    if (!/(^|\.)vimeo\.com$/.test(u.hostname)) return null;
    const match = u.pathname.match(/(\d+)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

// Returns a normalized descriptor for a video, or null if the URL/provider
// combination is invalid or unrecognized — callers must handle null by
// showing a "video unavailable" state rather than rendering a broken player.
export function resolveVideo(video) {
  if (!video || !video.url) return null;
  const provider = video.provider;

  if (provider === 'youtube') {
    const id = getYouTubeId(video.url);
    if (!id) return null;
    return {
      kind: 'iframe',
      embedUrl: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`,
      thumbnailUrl: video.posterUrl || `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
      title: video.title
    };
  }

  if (provider === 'vimeo') {
    const id = getVimeoId(video.url);
    if (!id) return null;
    return {
      kind: 'iframe',
      embedUrl: `https://player.vimeo.com/video/${id}?autoplay=1`,
      thumbnailUrl: video.posterUrl || '',
      title: video.title
    };
  }

  if (provider === 'mp4') {
    try {
      const u = new URL(video.url);
      if (u.protocol !== 'https:' || !u.pathname.toLowerCase().endsWith('.mp4')) return null;
    } catch {
      return null;
    }
    return {
      kind: 'file',
      src: video.url,
      thumbnailUrl: video.posterUrl || '',
      title: video.title
    };
  }

  if (provider === 'external') {
    try {
      const u = new URL(video.url);
      if (u.protocol !== 'https:') return null;
    } catch {
      return null;
    }
    return {
      kind: 'external',
      externalUrl: video.url,
      hostname: new URL(video.url).hostname,
      thumbnailUrl: video.posterUrl || '',
      title: video.title
    };
  }

  return null;
}
